import { createUseStyles, withTheme } from '@glu/theming';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useMemo } from 'react';
import moment from 'moment';
import classNames from 'classnames';
import locale from '@glu/locale';
import { AccessibilityText } from '@glu/utilities-react';
import styles from './Day.styles';
import { inRange, isValidSelection } from '../../utils/dateCompare';
import { useDropdownContext } from '../DropdownContext';

const useStyles = createUseStyles(styles);

const Day = ({
  dataQa,
  dateMax,
  dateMin,
  day,
  getClassNamesForDate,
  head,
  isSelectableDate,
  onClick,
  onMouseEnter,
  selectedEndDate,
  selectedStartDate,
  singleDatePicker,
  ...rest
}) => {
  const buttonRef = useRef();
  const { focusDate, onDayKeyDown, setFocusOn } = useDropdownContext();
  const classes = useStyles(rest);

  const isValid = isValidSelection(day.startOf('day'), [dateMin, dateMax]);
  const inCurrentMonth = day.month() === moment(head).month();
  const disabled = !isValid || isSelectableDate(day) || !inCurrentMonth;
  const noonOnDay = useMemo(
    () => moment(day).set('hour', 12).set('minute', 0).set('second', 0),
    [day]
  );
  const sameAsStart = selectedStartDate !== undefined && day.isSame(selectedStartDate, 'day');
  const sameAsEnd = selectedEndDate !== undefined && day.isSame(selectedEndDate, 'day');
  const isSelected = !disabled && (sameAsStart || sameAsEnd);
  const isSelectedStart = !disabled && sameAsStart && !sameAsEnd;
  const isSelectEnd = !disabled && !sameAsStart && sameAsEnd;
  const isInRange =
    !sameAsStart &&
    !sameAsEnd &&
    inCurrentMonth &&
    inRange(day, selectedStartDate, selectedEndDate);
  const isToday = !disabled && !isSelected && day.isSame(moment(), 'day');

  const buttonClassNames = classNames({
    [classes.cellButton]: true,
    [classes.disabled]: disabled,
    [classes.hidden]: !inCurrentMonth,
    [classes.isToday]: isToday,
    [classes.selected]: isSelected,
    [classes.selectStart]: isSelectedStart,
    [classes.selectEnd]: isSelectEnd,
    [classes.inRange]: isInRange,
  });

  const cellClassNames = () => {
    if (disabled) {
      return '';
    }
    const customClasses = getClassNamesForDate(day);
    if (typeof customClasses === 'string') {
      return customClasses;
    }
    if (Array.isArray(customClasses)) {
      return customClasses.join(' ');
    }
    return '';
  };

  useEffect(() => {
    if (!focusDate) {
      return;
    }
    if (day.isSame(focusDate)) {
      buttonRef.current.focus();
    }
  }, [day, focusDate, setFocusOn]);

  return (
    <td className={cellClassNames()}>
      <button
        ref={buttonRef}
        aria-current={isToday}
        className={buttonClassNames}
        data-qa={dataQa || `calendar-day-${day.format('YYYY-MM-DD')}`}
        disabled={disabled}
        onClick={() => {
          onClick(noonOnDay);
        }}
        onFocus={() => {
          setFocusOn('day');
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            onClick(noonOnDay);
            document.activeElement.blur();
            return;
          }
          onDayKeyDown(e, day);
        }}
        onMouseDown={(e) => e.preventDefault()}
        onMouseEnter={() => {
          onMouseEnter(noonOnDay);
        }}
        type="button"
      >
        <span aria-hidden>{day.format('D')}</span>
        <AccessibilityText>
          {moment(day).format('MMMM Do YYYY')}
          {isSelected && !isSelectedStart && !isSelectEnd && `, ${locale.get('selectedDate')}`}
          {!singleDatePicker && (
            <>
              {isSelectedStart && `, ${locale.get('selectedStartDate')}`}
              {isSelectEnd && `, ${locale.get('selectedEndDate')}`}
              {isInRange && `, ${locale.get('inSelectedRange')}`}
            </>
          )}
        </AccessibilityText>
      </button>
    </td>
  );
};

Day.propTypes = {
  dataQa: PropTypes.string,
  dateMax: PropTypes.string.isRequired,
  dateMin: PropTypes.string.isRequired,
  day: PropTypes.shape({
    format: PropTypes.func,
    isSame: PropTypes.func,
    month: PropTypes.func,
    startOf: PropTypes.func,
  }).isRequired,
  getClassNamesForDate: PropTypes.func.isRequired,
  head: PropTypes.shape({}).isRequired,
  isSelectableDate: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  onMouseEnter: PropTypes.func.isRequired,
  singleDatePicker: PropTypes.bool,
  selectedEndDate: PropTypes.shape({}),
  selectedStartDate: PropTypes.shape({}),
};

Day.defaultProps = {
  dataQa: '',
  singleDatePicker: false,
  selectedEndDate: undefined,
  selectedStartDate: undefined,
};

export default withTheme(Day);
